import React from 'react';
import { Button, Card, StatusChip, Text } from '@fragments';
import { IconAlertTriangle, IconArrowUpRight, IconInfoCircle, IconSparkles } from '@tabler/icons-react';
import PlanName, { planText } from '../../../../components/PlanName';
import { PLAN_BENEFITS } from '../../../../enums/plans';
import { futureFeatures } from '../../../../enums/futureFeatures';
import { formatCredits, formatDate, formatMoney } from '../../../../data/payments';
import './styles.scss';

const soon = feature => !!feature && futureFeatures.some(([key]) => key === feature);

// The subscription status as Stripe reports it; a scheduled cancellation reads first.
const CHIPS = {
  active: StatusChip.Success,
  trialing: StatusChip.Info,
  past_due: StatusChip.Alert,
  unpaid: StatusChip.Error,
  incomplete: StatusChip.Alert,
  paused: StatusChip.Default,
  canceling: StatusChip.Alert,
};

// The plan in force: name, price, what it includes (the plan dialog's benefits, unreleased
// ones marked "soon"), the cycle (monthly credits, next charge or end of access, subscriber
// since) and what needs the creator's attention — an upgrade held by the bank,
// a renewal Stripe could not charge, a cancellation scheduled. The footer block holds the
// way up (the next plan, or every plan from free) and the cancellation.
export default function PlanSummary({ studio: s, page }) {
  const { t } = s;
  const p = s.payments;
  const locale = s.locale.replace('_', '-');
  const sub = page.subscription;
  const paid = page.live;
  const info = p.planInfo(p.plan);
  const name = t(`planName_${p.plan}`);
  const canceling = paid && sub.cancel_at_period_end;
  const Chip = paid ? CHIPS[canceling ? 'canceling' : sub.status] : null;
  const date = iso => formatDate(iso, locale);
  const pending = sub.pending_change;
  // Monthly credits are a figure of the cycle below, not a row here.
  const benefits = paid ? (PLAN_BENEFITS[p.plan] || []).filter(benefit => benefit.key !== 'credits') : [];
  const failure = page.error && !page.confirming && <div className="studio-plan-summary__error" role="alert">
    <IconAlertTriangle aria-hidden="true" />
    <Text.span contrast={8}>{t(`error_${page.error}`, { defaultValue: t('error_generic') })}</Text.span>
  </div>;

  const facts = paid ? [
    { key: 'credits', label: t('billingMonthlyCredits'), value: formatCredits(info?.monthly_credits ?? sub.allowance?.monthly_units ?? 0, locale) },
    { key: 'renews', label: t(canceling ? 'billingEndsOn' : 'billingRenewsOn'), value: date(sub.current_period_end) },
    { key: 'since', label: t('billingSince'), value: date(sub.timestamps?.created) },
  ].filter(fact => fact.value) : [];

  // At most one notice, the most urgent first.
  const notice = (tone, text, actions) => <div className={`studio-plan-summary__notice is-${tone}`} role={tone === 'alert' ? 'alert' : 'status'}>
    {tone === 'alert' ? <IconAlertTriangle aria-hidden="true" /> : <IconInfoCircle aria-hidden="true" />}
    <div className="studio-plan-summary__notice-body">
      <Text.span contrast={8}>{text}</Text.span>
      {actions && <div className="studio-plan-summary__notice-actions">{actions}</div>}
    </div>
  </div>;
  const changeCard = <Button size="small" variant="text" color="overlay" onClick={page.openPortal}>{t('changeCard')}</Button>;
  const paidAlready = <Button size="small" variant="text" color="overlay" onClick={page.recheck}>{t('planPaid')}</Button>;
  const payInvoice = url => url && <Button size="small" variant="rounded" color="inverse" preventLoading endIcon={<IconArrowUpRight />} onClick={() => page.openUrl(url)}>{t('billingPayInvoice')}</Button>;
  const attention = !paid ? null
    : pending ? notice('alert', planText(t, pending.status === 'payment_failed' ? 'planActionFailed' : 'planActionRequired', t(`planName_${pending.plan}`)),
      <>{payInvoice(pending.payment_url)}{paidAlready}{changeCard}</>)
    : ['past_due', 'unpaid'].includes(sub.status) ? notice('alert', t('billingPastDueNotice'), <>{payInvoice(page.openInvoice?.hosted_invoice_url)}{changeCard}{paidAlready}</>)
    : sub.status === 'incomplete' ? notice('alert', t('billingIncompleteNotice'), <>{payInvoice(page.openInvoice?.hosted_invoice_url)}{paidAlready}</>)
    : canceling ? notice('info', planText(t, 'billingCancelingNotice', name, { date: date(sub.current_period_end) }),
      <Button size="small" variant="rounded" color="inverse" onClick={page.resume}>{t('billingResume')}</Button>)
    : null;

  const next = p.nextPlan;
  const actions = [
    page.canUpgrade && <Button key="upgrade" variant="rounded" color="ai" endIcon={<IconArrowUpRight />} preventLoading onClick={() => p.openUpgrade(next)}>
      {t('planUpgradeCta', { plan: t(`planShort_${next}`) })}
    </Button>,
    !paid && next && <Button key="plans" variant="rounded" color="ai" endIcon={<IconArrowUpRight />} preventLoading onClick={() => p.openUpgrade(next)}>{t('billingSeePlans')}</Button>,
    paid && !next && <Text.caption key="top" contrast={6}>{t('billingTopPlan')}</Text.caption>,
    page.entitled && !canceling && <Button key="cancel" className="studio-plan-summary__cancel" size="small" variant="text" color="overlay" preventLoading onClick={page.askCancel}>{t('billingCancel')}</Button>,
  ].filter(Boolean);

  return <Card split className="studio-plan-summary" footer={actions.length > 0 && <div className="studio-plan-summary__actions">{actions}</div>}>
    {paid && <span className="studio-plan-summary__glow" aria-hidden="true"><i className="is-violet" /><i className="is-cool" /></span>}
    <div className="studio-plan-summary__head">
      <Text.subheading contrast={6} left={<IconSparkles />}>{t('billingPlanEyebrow')}</Text.subheading>
      {Chip && <Chip preserveCase text={t(`billingStatus_${canceling ? 'canceling' : sub.status}`, { defaultValue: sub.status })} />}
    </div>
    <div className="studio-plan-summary__identity">
      <Text.h2 contrast={10} weight={500}>{paid ? <PlanName>{name}</PlanName> : name}</Text.h2>
      <Text.p contrast={7}>{paid ? t(`planTagline_${p.plan}`) : t('billingFreeText')}</Text.p>
    </div>
    {paid && sub.amount !== null && <div className="studio-plan-summary__price">
      <Text.h3 contrast={10} weight={500}>{formatMoney(sub.amount, sub.currency, locale)}</Text.h3>
      <Text.span contrast={6}>{t('perMonth')}</Text.span>
    </div>}
    {benefits.length > 0 && <ul className="studio-plan-summary__benefits" aria-label={t('billingIncluded')}>
      {benefits.map(({ key, icon: Icon, feature }) => <li key={key}>
        <Icon aria-hidden="true" />
        <Text.span contrast={8}>{t(`planBenefit_${p.plan}_${key}`)}</Text.span>
        {soon(feature) && <Text.caption contrast={5}>{t('soon')}</Text.caption>}
      </li>)}
    </ul>}
    {facts.length > 0 && <dl className="studio-plan-summary__facts">
      {facts.map(fact => <div key={fact.key}>
        <dt><Text.caption contrast={6}>{fact.label}</Text.caption></dt>
        <dd><Text.label contrast={9}>{fact.value}</Text.label></dd>
      </div>)}
    </dl>}
    {attention}
    {failure}
  </Card>;
}
